/* eslint-disable jsx-a11y/click-events-have-key-events */
import { createUseStyles } from '@glu/theming';
import { createPortal } from 'react-dom';
import React, {
  useEffect, useRef, useState
} from 'react';
import { AccessibilityText, debounce } from '@glu/utilities-react';
import locale from '@glu/locale';
import classnames from 'classnames';
import RangeLayout from './RangeLayout';
import DateSelect from './DateSelect';
import styles from './Dropdown.styles';

const useStyles = createUseStyles(styles);

const Dropdown = (props) => {
  const {
    alwaysAlignRight,
    alwaysOpenUp,
    clickOutsideToClose,
    dropdownClassName,
    fieldRef,
    forcePositionX,
    forcePositionY,
    labelText,
    onBlur,
    onClose,
    ranges,
    screenReaderLabel,
    usePortal
  } = props;
  const classes = useStyles();
  const popupRef = useRef();

  const [alignRight, setAlignRight] = useState(alwaysAlignRight);
  const [onTop, setOnTop] = useState(false);
  const [positionLeft, setPositionLeft] = useState();
  const [positionRight, setPositionRight] = useState();
  const [positionTop, setPositionTop] = useState();
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!usePortal) {
      const {
        height,
        left: popupLeft,
        right,
        top: popupTop,
        width
      } = popupRef.current.getBoundingClientRect();
      const { innerHeight, innerWidth } = window;
      const overlapsBottomEdge = popupTop + height > innerHeight
      || popupTop > innerHeight - popupTop;
      const overflowsRightEdge = popupLeft > innerWidth - right
      || innerWidth - right < width;

      /* istanbul ignore next */
      if (overflowsRightEdge && !forcePositionX) {
        setAlignRight(true);
      }

      /* istanbul ignore next */
      if ((overlapsBottomEdge || alwaysOpenUp) && !forcePositionY) {
        setOnTop(true);
      }
    }
  }, [alwaysAlignRight, alwaysOpenUp, forcePositionX, forcePositionY]);

  const resetDropdownPosition = () => {
    if (usePortal && fieldRef.current?.querySelector) {
      const field = fieldRef.current.getBoundingClientRect();
      const popup = popupRef.current.getBoundingClientRect();
      const { innerWidth } = window;
      const overflowsRightEdge = popup.left > innerWidth - popup.right;
      if (overflowsRightEdge) {
        setAlignRight(true);
        setPositionLeft('auto');
        setPositionRight(0);
      } else {
        setPositionLeft(field.x);
        setPositionRight('auto');
        setAlignRight(false);
      }
      setPositionTop(field.y + field.height);
    }
  };

  useEffect(() => {
    resetDropdownPosition();

    window.addEventListener('scroll', debounce(resetDropdownPosition, 100));
    window.addEventListener('resize', debounce(resetDropdownPosition, 100));
    return () => {
      window.removeEventListener('scroll', debounce(resetDropdownPosition, 100));
      window.removeEventListener('resize', debounce(resetDropdownPosition, 100));
    };
  }, [usePortal, fieldRef, resetDropdownPosition, setPositionLeft, setPositionTop]);

  const handleSetEditing = (value) => {
    setEditing(value);
    resetDropdownPosition();
  };

  const handleOverLayClick = (e) => {
    e.preventDefault();
    onClose();
    onBlur();
  };

  /* istanbul ignore next */
  const Popup = (
    <div
      className={classnames(
        classes.overlay,
        dropdownClassName,
        {
          [classes.clickOutsideToClose]: clickOutsideToClose,
          [classes.usePortal]: usePortal
        }
      )}
    >
      <button
        onClick={handleOverLayClick}
        type="button"
        className={classnames(
          classes.closeButton, { [classes.clickOutsideToClose]: clickOutsideToClose }
        )}
      >
        <AccessibilityText>{locale.get('closePopup')}</AccessibilityText>
      </button>
      <div
        ref={popupRef}
        data-qa="popup"
        className={
          classnames(
            classes.popup,
            onTop ? classes.onTop : classes.onBottom,
            alignRight ? classes.alignRight : classes.alignLeft,
            {
              [classes.labelText]: !!labelText && !screenReaderLabel,
              [classes.screenReaderLabel]: screenReaderLabel,
              [classes.usePortal]: usePortal
            }
          )
          }
        style={{ left: positionLeft, right: positionRight, top: positionTop }}
      >
        {ranges
          ? (
            <RangeLayout
              {...props}
              alignRight={alignRight}
              editing={editing}
              onTop={onTop}
              setEditing={handleSetEditing}
            />
          )
          : <DateSelect {...props} />}
      </div>
    </div>
  );

  /* istanbul ignore next */
  return usePortal ? createPortal(Popup, document.querySelector('body')) : Popup;
};

export default Dropdown;
